import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type DealFormDataWithTargeting, dealFormField, ENABLED_DATA_PARTNERS_SECTION_ID } from '@openx/types/deal.ts';
import { PackageFields } from '@openx/types/package.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import type { JSX } from 'react';
import { useWatch } from 'react-hook-form';
import { EnabledDataPartners } from '../../components/EnableDataPartners/EnabledDataPartners.tsx';
import { useContextualPricingState } from '../../hooks/useContextualPricingState.ts';

const CONTEXTUAL_SEGMENTS_FIELD =
  `${dealFormField.TARGETING}.${PackageFields.TARGETING}.${TargetingFields.CONTEXTUAL_SEGMENTS}` as const;

type EnabledDataPartnersFormSectionProps = {
  pricingInfo: AudiencePricing | undefined;
  hasMultiProviderPricingData: boolean;
  loadingAudienceCheck: boolean;
  isAdmin?: boolean;
};

export const EnabledDataPartnersFormSection = ({
  pricingInfo,
  hasMultiProviderPricingData,
  loadingAudienceCheck,
  isAdmin = false,
}: EnabledDataPartnersFormSectionProps): JSX.Element | null => {
  // the account of the deal, as the targeting block prices it, so the fees alert and this section agree
  const observableAccountUid = useWatch<DealFormDataWithTargeting, typeof dealFormField.ACCOUNT_UID>({
    name: dealFormField.ACCOUNT_UID,
  });
  const observableContextualSegments = useWatch<DealFormDataWithTargeting, typeof CONTEXTUAL_SEGMENTS_FIELD>({
    name: CONTEXTUAL_SEGMENTS_FIELD,
  });
  const contextualPricing = useContextualPricingState({
    segments: observableContextualSegments,
    accountUid: observableAccountUid ?? undefined,
  });

  return (
    <FormSection
      divider
      title="Curated Deal Fees for Enabled Data Partners"
      data-test="enable-data-partners"
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      id={ENABLED_DATA_PARTNERS_SECTION_ID}
    >
      <EnabledDataPartners
        pricingInfo={pricingInfo}
        hasMultiProviderPricingData={hasMultiProviderPricingData}
        loadingAudienceCheck={loadingAudienceCheck}
        contextualPricing={contextualPricing}
        isAdmin={isAdmin}
      />
    </FormSection>
  );
};
